import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { getStorage } from "../auth/utils";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { useTranslation } from "./Internationalization";
import { endImpersonationSession } from "../auth/impersonationClub";
import { Button } from "../shadcn/components/ui/button";

const SuperAdminContext = createContext();
const { Provider } = SuperAdminContext;

// The banner is sticky at the top. The fixed mobile top bar and desktop
// sidebar read its height from this variable so it never covers them.
const BANNER_HEIGHT_VAR = "--impersonation-banner-height";

const useBannerHeightVar = (bannerRef, visible) => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const banner = bannerRef.current;
    if (!visible || !banner) return undefined;
    const update = () =>
      root.style.setProperty(BANNER_HEIGHT_VAR, `${banner.offsetHeight}px`);
    update();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [bannerRef, visible]);
};

const SuperAdminProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const { t } = useTranslation();
  const { setImpersonate } = useContext(AuthContext);
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  useBannerHeightVar(bannerRef, Boolean(user));

  useEffect(() => {
    const impersonate = getStorage().getItem("impersonate");
    if (impersonate) {
      setUser(JSON.parse(impersonate));
    }
  }, []);

  const handleSetUser = (user) => {
    if (user) {
      getStorage().setItem("impersonate", JSON.stringify(user));
    } else {
      getStorage().removeItem("impersonate");
    }

    setUser(user);
  };

  const handleLeaveImpersonate = () => {
    setUser(null);
    setTimeout(() => {
      endImpersonationSession(getStorage());
      setImpersonate(null);
      navigate("/");
    }, 200);
  };

  return (
    <Provider value={{ user, setUser: handleSetUser }}>
      <div className="flex flex-col">
        {user && (
          // The old Warning[700] banner colour. DS Manager has no warning
          // token yet: pending, see "Waiting for the user" in the plan.
          <div
            ref={bannerRef}
            className="sticky top-0 z-[1300] flex flex-wrap items-center justify-between gap-x-4 bg-[#B54708] px-4 py-1 text-sm text-white"
          >
            <span className="min-w-0 break-words">
              {user?.firstName} {user?.lastName}
            </span>
            <Button
              type="button"
              variant="link"
              size="xs"
              className="px-0 text-white focus-visible:ring-white/50"
              onClick={handleLeaveImpersonate}
            >
              {t("leave_impersonate")}
            </Button>
          </div>
        )}
        {children}
      </div>
    </Provider>
  );
};

export const useSuperAdmin = () => {
  return useContext(SuperAdminContext);
};

export default SuperAdminProvider;
